import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Globe, Check, Sparkles, Building, Mail, Phone, 
  MapPin, Briefcase, Eye, Edit, Plus, Trash2, ArrowLeft, 
  Loader2, ArrowRight, Save, Palette, Type, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getAuthHeaders, getUser } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AutoWebsiteBuilder = () => {
  const navigate = useNavigate();
  const currentUser = getUser();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [website, setWebsite] = useState(null);
  
  // Wizard steps: 'info' -> 'preview' -> 'published'
  const [step, setStep] = useState('info');
  
  // Optional business information
  const [optionalInfo, setOptionalInfo] = useState({
    services: [],
    about: '',
    portfolio: [],
    testimonials: [],
    hours: '',
    team: []
  });
  
  // Template customization
  const [templateData, setTemplateData] = useState({
    heroHeading: '',
    heroSubheading: '',
    heroBackgroundColor: '#2563eb',
    aboutHeading: 'About Us',
    servicesHeading: 'Our Services',
    portfolioHeading: 'Our Work',
    contactHeading: 'Get In Touch',
    accentColor: '#2563eb',
    textColor: '#1f2937',
    backgroundColor: '#ffffff'
  });
  
  // Editing state
  const [editingSection, setEditingSection] = useState(null);

  useEffect(() => {
    fetchUserProfile();
    checkExistingWebsite();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: getAuthHeaders()
      });
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load your profile');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingWebsite = async () => {
    try {
      const response = await axios.get(`${API}/auto-websites/my-website`, {
        headers: getAuthHeaders()
      });
      if (response.data.website) {
        setWebsite(response.data.website);
        if (response.data.website.optionalInfo) {
          setOptionalInfo(response.data.website.optionalInfo);
        }
      }
    } catch (error) {
      // No website exists yet, that's fine
      console.log('No existing website');
    }
  };

  const generatePreview = () => {
    if (!userProfile) {
      toast.error('Please complete your business card profile first');
      navigate('/profile/edit');
      return;
    }

    if (!userProfile.name || !userProfile.businessName) {
      toast.error('Please add your name and business name to your profile');
      navigate('/profile/edit');
      return;
    }

    // Set default template data from profile
    setTemplateData({
      ...templateData,
      heroHeading: userProfile.businessName || '',
      heroSubheading: userProfile.businessCategory || 'Professional Services',
    });

    // Move to preview step
    setStep('preview');
    toast.success('Preview generated! Edit any section by clicking on it.');
  };

  const publishWebsite = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/auto-websites/generate`,
        {
          optionalInfo,
          templateData
        },
        { headers: getAuthHeaders() }
      );

      setWebsite(response.data.website);
      setStep('published');
      toast.success('🎉 Your website is published!');
    } catch (error) {
      console.error('Error publishing website:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish website');
    } finally {
      setGenerating(false);
    }
  };

  const updateOptionalInfo = async () => {
    try {
      await axios.put(
        `${API}/auto-websites/update-info`,
        { optionalInfo },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Business information updated! Regenerating website...');
      await generateWebsite();
    } catch (error) {
      console.error('Error updating info:', error);
      toast.error('Failed to update information');
    }
  };

  const addService = () => {
    setOptionalInfo({
      ...optionalInfo,
      services: [...optionalInfo.services, { title: '', description: '', icon: '⭐' }]
    });
  };

  const updateService = (index, field, value) => {
    const updated = [...optionalInfo.services];
    updated[index][field] = value;
    setOptionalInfo({ ...optionalInfo, services: updated });
  };

  const removeService = (index) => {
    const updated = optionalInfo.services.filter((_, i) => i !== index);
    setOptionalInfo({ ...optionalInfo, services: updated });
  };

  const addPortfolioItem = () => {
    setOptionalInfo({
      ...optionalInfo,
      portfolio: [...optionalInfo.portfolio, { title: '', description: '', image: '' }]
    });
  };

  const updatePortfolioItem = (index, field, value) => {
    const updated = [...optionalInfo.portfolio];
    updated[index][field] = value;
    setOptionalInfo({ ...optionalInfo, portfolio: updated });
  };

  const removePortfolioItem = (index) => {
    const updated = optionalInfo.portfolio.filter((_, i) => i !== index);
    setOptionalInfo({ ...optionalInfo, portfolio: updated });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <Globe className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Auto Website Generator</h1>
          </div>
          <p className="text-gray-600">
            Your website is automatically created from your digital business card
          </p>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Profile Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5" />
                Your Business Card Info
              </CardTitle>
            </CardHeader>
            <CardContent>
              {userProfile ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <h3 className="font-bold text-lg text-gray-900">{userProfile.name}</h3>
                    <p className="text-blue-600 font-medium">{userProfile.businessName}</p>
                    {userProfile.businessCategory && (
                      <p className="text-sm text-gray-600 mt-1">{userProfile.businessCategory}</p>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    {userProfile.email && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <Mail className="w-4 h-4" />
                        {userProfile.email}
                      </div>
                    )}
                    {userProfile.phone && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <Phone className="w-4 h-4" />
                        {userProfile.phone}
                      </div>
                    )}
                    {userProfile.address && (
                      <div className="flex items-center gap-2 text-gray-700">
                        <MapPin className="w-4 h-4" />
                        {userProfile.address}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => navigate('/profile/edit')}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Business Card
                  </Button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">Complete your business card first</p>
                  <Button onClick={() => navigate('/profile/edit')}>
                    Create Business Card
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right: Website Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Website Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {website ? (
                <div className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="font-bold text-green-900">Website Ready!</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Your website was created on {new Date(website.createdAt).toLocaleDateString()}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => window.open(`/auto-site/${website.id}`, '_blank')}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Website
                      </Button>
                      <Button
                        onClick={generateWebsite}
                        disabled={generating}
                        variant="outline"
                        className="flex-1"
                      >
                        {generating ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-2" />
                        )}
                        Regenerate
                      </Button>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600">
                    <p className="font-medium mb-1">Your website includes:</p>
                    <ul className="space-y-1">
                      <li>✓ Hero section with your business info</li>
                      <li>✓ About section</li>
                      <li>✓ Contact information</li>
                      {optionalInfo.services?.length > 0 && <li>✓ Services section</li>}
                      {optionalInfo.portfolio?.length > 0 && <li>✓ Portfolio/Gallery</li>}
                      {optionalInfo.testimonials?.length > 0 && <li>✓ Testimonials</li>}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Sparkles className="w-12 h-12 mx-auto text-blue-600 mb-4" />
                  <p className="text-gray-600 mb-4">
                    Generate your professional website in seconds using your business card
                  </p>
                  <Button
                    onClick={generateWebsite}
                    disabled={generating || !userProfile}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating Website...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate My Website
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Optional Business Information */}
        <Card className="mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Additional Business Information (Optional)
              </CardTitle>
              <Button
                onClick={() => setShowOptionalInfo(!showOptionalInfo)}
                variant="outline"
                size="sm"
              >
                {showOptionalInfo ? 'Hide' : 'Show'}
              </Button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              Add more details to enhance your website content
            </p>
          </CardHeader>
          {showOptionalInfo && (
            <CardContent>
              <div className="space-y-6">
                {/* About Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    About Your Business
                  </label>
                  <Textarea
                    value={optionalInfo.about}
                    onChange={(e) => setOptionalInfo({ ...optionalInfo, about: e.target.value })}
                    placeholder="Tell your story, describe your mission, values, and what makes you unique..."
                    rows={4}
                  />
                </div>

                {/* Services */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Services/Products
                    </label>
                    <Button onClick={addService} size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Service
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {optionalInfo.services.map((service, index) => (
                      <div key={index} className="border border-gray-200 p-4 rounded-lg">
                        <div className="flex gap-2 mb-2">
                          <Input
                            placeholder="Service name"
                            value={service.title}
                            onChange={(e) => updateService(index, 'title', e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            onClick={() => removeService(index)}
                            variant="ghost"
                            size="sm"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <Textarea
                          placeholder="Service description"
                          value={service.description}
                          onChange={(e) => updateService(index, 'description', e.target.value)}
                          rows={2}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Portfolio */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Portfolio/Work Samples
                    </label>
                    <Button onClick={addPortfolioItem} size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {optionalInfo.portfolio.map((item, index) => (
                      <div key={index} className="border border-gray-200 p-4 rounded-lg">
                        <div className="flex gap-2 mb-2">
                          <Input
                            placeholder="Project name"
                            value={item.title}
                            onChange={(e) => updatePortfolioItem(index, 'title', e.target.value)}
                            className="flex-1"
                          />
                          <Button
                            onClick={() => removePortfolioItem(index)}
                            variant="ghost"
                            size="sm"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <Input
                          placeholder="Image URL"
                          value={item.image}
                          onChange={(e) => updatePortfolioItem(index, 'image', e.target.value)}
                          className="mb-2"
                        />
                        <Textarea
                          placeholder="Project description"
                          value={item.description}
                          onChange={(e) => updatePortfolioItem(index, 'description', e.target.value)}
                          rows={2}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Business Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Hours
                  </label>
                  <Textarea
                    value={optionalInfo.hours}
                    onChange={(e) => setOptionalInfo({ ...optionalInfo, hours: e.target.value })}
                    placeholder="e.g., Monday-Friday: 9AM-5PM, Saturday: 10AM-2PM"
                    rows={2}
                  />
                </div>

                {/* Save Button */}
                <div className="flex gap-3">
                  <Button
                    onClick={updateOptionalInfo}
                    disabled={generating}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Updating Website...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Update & Regenerate Website
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AutoWebsiteBuilder;
